import { Vec3, vec3, sub, scale, cross, dot, normalize, length, UNIT_Z } from '../math/vec3'
import { Quat, unrotate, rotate } from '../math/quat'

const TAU = Math.PI * 2

/** Verticale locale: dal centro del corpo verso l'esterno. */
export const localUp = (position: Vec3): Vec3 => normalize(position)

/**
 * Nord locale: la componente del polo (+Z) perpendicolare alla verticale.
 * Ai poli la definizione degenera e si sceglie una direzione di comodo,
 * perche restituire NaN spaccherebbe la navball proprio dove serve.
 */
export function localNorth(position: Vec3): Vec3 {
  const up = localUp(position)
  const proj = sub(UNIT_Z, scale(up, dot(UNIT_Z, up)))
  return length(proj) < 1e-9 ? normalize(cross(vec3(1, 0, 0), up)) : normalize(proj)
}

/** Est locale, tale che (est, nord, alto) sia una terna destrorsa. */
export const localEast = (position: Vec3): Vec3 =>
  normalize(cross(localNorth(position), localUp(position)))

export interface Attitude {
  /** angolo del muso sopra l'orizzonte, rad, in [-π/2, π/2] */
  readonly pitch: number
  /** rotta sulla bussola locale, rad, in [0, 2π) */
  readonly heading: number
  /** rollio attorno al muso, rad */
  readonly roll: number
}

export function attitudeOf(position: Vec3, orientation: Quat): Attitude {
  const up = localUp(position)
  const north = localNorth(position)
  const east = localEast(position)

  const nose = rotate(orientation, vec3(0, 1, 0))

  // beccheggio con atan2 fra componente verticale e componente orizzontale,
  // non con asin: allo zenit la derivata dell'arcoseno esplode e si perdono
  // meta delle cifre proprio dove il razzo passa la maggior parte del lancio.
  const verticale = dot(nose, up)
  const orizzontale = length(sub(nose, scale(up, verticale)))
  const pitch = Math.atan2(verticale, orizzontale)

  const heading = Math.atan2(dot(nose, east), dot(nose, north))

  // rollio: angolo fra il "sopra" del veicolo e la verticale locale,
  // misurato nel piano perpendicolare al muso. Col muso allo zenit il
  // riferimento sparisce e il rollio non e definito: vale zero, non NaN.
  const vesselUp = rotate(orientation, vec3(0, 0, 1))
  const refUp = sub(up, scale(nose, dot(up, nose)))
  const roll = length(refUp) < 1e-9
    ? 0
    : Math.atan2(dot(cross(normalize(refUp), vesselUp), nose), dot(normalize(refUp), vesselUp))

  return { pitch, heading: ((heading % TAU) + TAU) % TAU, roll }
}

export interface NavballPoint {
  /** coordinate sul disco unitario, con +x a destra e +y in alto */
  readonly x: number
  readonly y: number
  /** false se la direzione sta dietro al veicolo */
  readonly visible: boolean
}

/**
 * Proietta una direzione ESPRESSA IN SISTEMA CORPO sul disco della navball.
 * Il muso (+Y) finisce al centro; +X del corpo finisce a sinistra, perche la
 * navball si guarda da dietro, come un pilota guarda il proprio strumento.
 */
export function navballProject(bodyDirection: Vec3): NavballPoint {
  const d = normalize(bodyDirection)
  return { x: -d.x, y: d.z, visible: d.y >= 0 }
}

/** Direzione (in sistema corpo) di un vettore espresso nel mondo. */
export const toBody = (orientation: Quat, worldDirection: Vec3): Vec3 =>
  unrotate(orientation, normalize(worldDirection))
